import { GetStaticPaths, NextPage } from 'next';
import { useRouter } from 'next/router';

import { ActiveJobPosition, RestWorldRating, globalServices, jobPositionService } from '@restworld/data-services';
import { EntityId, JobPosition, Restaurant, Variable } from '@restworld/utility-types';

import { JobPositionSEO, JobPositionPage as JobPositionPageComponent, Loader } from '../../components';
import { getServerTranslations } from '../../lib/i18n/getServerTranslations';
import HirematicPixel from '../../components/job-position/analytics/hirematic-pixel';

export const baseUrl = process.env.NEXT_PUBLIC_BASE_API_URL;

export const JobPositionPage: NextPage<PosizionePageProps> = (props: PosizionePageProps) => {
  const router = useRouter();
  if (router.isFallback) return <Loader />;
  return (
    <>
      <JobPositionPageComponent {...props} />
      {props?.jobPositionStatic ? <JobPositionSEO {...props} /> : null}
      {props?.jobPositionStatic?.hirematic_flag && <HirematicPixel />}
    </>
  );
};
export default JobPositionPage;

export const getStaticPaths: GetStaticPaths = async () => {
  try {
    const positions = await jobPositionService({ baseUrl }).indexPublicJobPositions();
    const paths = Array.isArray(positions?.data)
      ? positions?.data?.reduce((acc: StaticProps[], jobPosition: ActiveJobPosition) => {
          acc = [
            ...acc,
            ...(jobPosition.semantic_unique_name
              ? [
                  {
                    params: { slug: jobPosition.semantic_unique_name },
                  },
                ]
              : []),
          ];
          return acc;
        }, [])
      : [];
    return {
      paths: paths ?? [],
      fallback: true,
    };
  } catch (error) {
    console.error(error, 'error fetching job positions for paths');
    return {
      paths: [],
      fallback: false,
    };
  }
};

export const getStaticProps = async ({ params: { slug }, locale }) => {
  const transConfig = await getServerTranslations(locale, [
    'common',
    'job-position-page',
    'onboarding-page',
    'auth-flow',
    'job-explorer-page',
  ]);
  try {
    let staticProps = {
      revalidate: 60,
      props: {
        jobPositionStatic: null,
        errorCode: null,
        restworldGoogleRatingData: null,
        slug,
        notFound: null,
        jobActiveStatus: null,
        domainInterest: null,
      },
    };
    if (!slug) return staticProps;
    try {
      const jobPositionStatic: JobPosition = (await jobPositionService({ baseUrl }).showJobPosition(slug as string))
        .data;

      const jobActiveStatus: boolean = (
        await jobPositionService({ baseUrl }).showJobPositionStatus(jobPositionStatic.id)
      ).data;
      const restworldGoogleRatingData: RestWorldRating = (await globalServices({ baseUrl }).fetchRestworldRating())
        .data;

      const domainInterest = (await globalServices({ baseUrl }).fetchVariableOptions('domain_interest')).data;

      staticProps = {
        ...staticProps,
        props: {
          ...staticProps.props,
          ...transConfig,
          ...(jobPositionStatic ? { jobPositionStatic: minifyJobPosition(jobPositionStatic) } : {}),
          restworldGoogleRatingData,
          jobActiveStatus,
          slug,
          domainInterest,
        },
      };
    } catch (error) {
      console.error(`Error getting static job position data for ${slug}`, error);
      staticProps = {
        ...staticProps,
        props: {
          ...staticProps.props,
          ...transConfig,
          errorCode: 404,
          notFound: true,
        },
      };
    }
    return staticProps;
  } catch (error) {
    console.error(error, 'error fetching job position');
    return {
      props: {
        ...transConfig,
        slug,
      },
    };
  }
};

const minifyJobPosition = (jp: Partial<JobPosition>) => {
  const { restaurant, id } = jp;
  try {
    const partial: Partial<Replace<JobPosition, 'restaurant', Partial<Restaurant>>> = {
      id,
      restaurant: {
        restaurant_name: restaurant?.restaurant_name ?? '',
        address: restaurant?.address ?? '',
        postal_code: restaurant?.postal_code ?? '',
        locality: restaurant?.locality ?? '',
        country: restaurant?.country ?? '',
        administrative_area_level_1: restaurant?.administrative_area_level_1 ?? '',
        ...(restaurant?.administrative_area_level_2
          ? { administrative_area_level_2: restaurant?.administrative_area_level_2 }
          : {}),
        ...(restaurant?.address_lat ? { address_lat: restaurant?.address_lat } : {}),
        ...(restaurant?.address_lon ? { address_lon: restaurant?.address_lon } : {}),
        ...(restaurant?.approximate_lon ? { approximate_lon: restaurant?.approximate_lon } : {}),
        ...(restaurant?.approximate_lat ? { approximate_lat: restaurant?.approximate_lat } : {}),
        restaurant_logo: restaurant?.restaurant_logo ?? '',
        ...(restaurant?.inserted_at ? { inserted_at: restaurant?.inserted_at } : {}),
        ...(restaurant?.pictures ? { pictures: restaurant?.pictures } : {}),
        ...(restaurant?.client_types ? { client_types: restaurant?.client_types } : {}),
        ...(restaurant?.types ? { types: restaurant?.types } : {}),
        ...(restaurant?.staff_size_floor ? { staff_size_floor: restaurant?.staff_size_floor } : {}),
        ...(restaurant?.staff_size_kitchen ? { staff_size_kitchen: restaurant?.staff_size_kitchen } : {}),
        ...(restaurant?.covers_per_service_var ? { covers_per_service_var: restaurant?.covers_per_service_var } : {}),
      },
      ...(jp?.occupation ? { occupation: jp.occupation } : {}),
      ...(jp.payment_type_var ? { payment_type_var: jp.payment_type_var } : {}),
      ...(jp.contract_type_var ? { contract_type_var: jp.contract_type_var } : {}),
      ...(jp.shift_category_var ? { shift_category_var: jp.shift_category_var ?? null } : {}),
      ...(jp.days_required_per_week_number ? { days_required_per_week_number: jp.days_required_per_week_number } : {}),
      job_title: jp.job_title ?? '',
      ...(jp.benefits ? { benefits: jp.benefits } : {}),
      published_at: jp.published_at ?? '',
      salary: jp.salary ?? null,
      min_salary: jp.min_salary ?? null,
      max_salary: jp.max_salary ?? null,
      ...(jp.salary_type ? { salary_type: jp.salary_type } : {}),
      salary_month_number: jp.salary_month_number ?? null,
      ...(jp.show_restaurant_details ? { show_restaurant_details: jp.show_restaurant_details } : {}),
      job_description: jp.job_description ?? '',
      job_contract_description: jp.job_contract_description ?? '',
      status_key: jp.status_key ?? null,
      meta_title: jp.meta_title ?? null,
      meta_description: jp.meta_description ?? null,
      meta_image_url: jp.meta_image_url ?? null,
      restaurant_id: restaurant?.id ?? null,
      shifts: jp.shifts ?? [],
      benefits: jp.benefits ?? [],
      required_skills: jp.required_skills ?? [],
      optional_skills: jp.optional_skills ?? [],
      other_requirements: jp.other_requirements ?? [],
      education: jp.education ?? [],
      languages: jp.languages ?? [],
      certificates: jp.certificates ?? [],
      manage_using_klaaryo: jp.manage_using_klaaryo ?? false,
      hirematic_flag: jp.hirematic_flag ?? false,
      ...(jp.assigned_to_user ? { assigned_to_user: jp.assigned_to_user } : {}),
      ...(jp.salary_rating ? { salary_rating: jp.salary_rating } : {}),
      ...(jp.career_rating ? { career_rating: jp.career_rating } : {}),
      ...(jp.worklife_balance_rating ? { worklife_balance_rating: jp.worklife_balance_rating } : {}),
      ...(jp.career_development_description
        ? { career_development_description: jp.career_development_description }
        : {}),
      ...(jp.csm_insights ? { csm_insights: jp.csm_insights } : {}),
      ...(jp.job_type_var ? { job_type_var: jp.job_type_var } : {}),
      ...(jp.days_required_per_week ? { days_required_per_week: jp.days_required_per_week } : {}),
      ...(jp.shift_notes ? { shift_notes: jp.shift_notes } : {}),
    };
    return partial;
  } catch (error) {
    console.error(error, 'error minifying job position');
    return null;
  }
};
type StaticProps = {
  params: {
    slug: string;
  };
};
export interface PosizionePageProps {
  jobPositionStatic: Partial<JobPosition>;
  slug?: EntityId;
  errorCode?: number | null;
  restworldGoogleRatingData?: RestWorldRating | null;
  jobActiveStatus?: boolean | null;
  notFound?: boolean | null;
  domainInterest?: Variable[] | null;
}

type Identity<T> = { [P in keyof T]: T[P] };
type Replace<T, K extends keyof T, TReplace> = Identity<
  Pick<T, Exclude<keyof T, K>> & {
    [P in K]: TReplace;
  }
>;
